import type { ProductFormSchema } from "schemas/generated/product";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";

import { useProductFormEdit } from "../hooks/useProductFormEdit";
import { useProductUpdate } from "../hooks/useProductUpdate";

interface ProductFormUnsavedChangesModalProps {
  blocker: { proceed?: () => void; reset?: () => void };
  uploadingFileIds: string[];
}

export function ProductFormUnsavedChangesModal({
  blocker,
  uploadingFileIds,
}: ProductFormUnsavedChangesModalProps) {
  const {
    product,
    creatingNewProduct,
    handleReactHookFormSubmit,
    reset,
    isProductValid: isValid,
  } = useProductFormEdit();

  const isMobile = useIsMobile();
  const isUploadingFiles = uploadingFileIds.length > 0;

  const { updateProduct } = useProductUpdate();

  const handleProductSave = React.useCallback(
    async (productData: ProductFormSchema) => {
      await updateProduct({
        product,
        formData: productData,
        creatingNewProduct,
      });
      reset(productData);
    },
    [updateProduct, product, creatingNewProduct, reset],
  );

  const handleModalSave = React.useCallback(async () => {
    await handleReactHookFormSubmit(handleProductSave)();
    if (isValid) {
      blocker.proceed?.();
    }
  }, [handleReactHookFormSubmit, handleProductSave, blocker, isValid]);

  const handleModalDiscard = React.useCallback(() => {
    reset();
    blocker.proceed?.();
  }, [reset, blocker]);

  const handleModalCancel = React.useCallback(() => {
    blocker.reset?.();
  }, [blocker]);

  const modalContent = React.useMemo(() => {
    const description = isUploadingFiles
      ? "You have unsaved progress. If you leave now, your edits and file uploads will be lost."
      : "You have unsaved progress. If you leave now, your edits will be lost.";

    return {
      title: "Discard changes?",
      description,
      footer: (
        <div className={twMerge("flex gap-3", isMobile && "justify-center")}>
          {!isUploadingFiles && (
            <Button
              variant="primary"
              size="base"
              onClick={() => void handleModalSave()}
            >
              Save Changes
            </Button>
          )}
          <Button
            variant="destructive"
            size="base"
            onClick={handleModalDiscard}
          >
            Discard
          </Button>
          {isUploadingFiles && (
            <Button variant="secondary" size="base" onClick={handleModalCancel}>
              Cancel
            </Button>
          )}
        </div>
      ),
    };
  }, [
    isUploadingFiles,
    handleModalSave,
    handleModalDiscard,
    handleModalCancel,
    isMobile,
  ]);

  return (
    <Modal
      size="sm"
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          handleModalCancel();
        }
      }}
      title={modalContent.title}
      description={modalContent.description}
      footer={modalContent.footer}
    />
  );
}
